import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router';
import { Spinner, Tabs, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { BackButton, Box, Center, FloatingContainer, Row, Screen } from '~/commonComponents';
import { JobPostingStatus } from '~/domain/PermanentJobs';
import { useBlockBackNavigation, useJobProfessionDisplay } from '~/hooks';
import { ProfessionTypeEnum } from '~/models';
import {
  CandidateTabView,
  CloseJobFeedbackPopup,
  CloseJobSuccessPopup,
  ConfirmCloseJobPopup,
  JobDetailsTabView,
  JobOverviewHeader,
  OnsiteInterviewTabView,
  SelectCandidatesPopup,
  useCloseJobFlow,
} from './components';
import { TJobOverviewPageRoutes } from './Types';
import { TAB, useJobOverview } from './useJobOverview';
import { useJobOverviewData } from './useJobOverviewData';
import { useSafeLocationState } from './useSafeLocationState';
import { CreateOnsiteInterviewSteps } from '../CreateOnsiteInterview';
import { CreatePermanentJobPostingSteps } from '../CreatePosting';

const BLOCKED_ROUTE_PATTERNS = [
  `/permanent-jobs/postings/:id/create/${CreatePermanentJobPostingSteps.BasicInformation}`,
  `/permanent-jobs/postings/:id/create/${CreatePermanentJobPostingSteps.JobDescription}`,
  `/permanent-jobs/postings/:id/create/${CreatePermanentJobPostingSteps.ReviewAndPayment}`,
  `/permanent-jobs/postings/:id/create/${CreatePermanentJobPostingSteps.WorkDetails}`,
  `/permanent-jobs/postings/:id/create`,
  `/permanent-jobs/postings/:id/onsite-interviews/create/${CreateOnsiteInterviewSteps.Reviews}`,

  `/permanent-jobs/postings/:id/onsite-interviews/create/${CreateOnsiteInterviewSteps.WorkDetails}`,
  `/permanent-jobs/postings/:id/onsite-interviews/create`,
];

export const JobOverview = () => {
  const queryClient = useQueryClient();
  const { postingId } = useParams<TJobOverviewPageRoutes>();

  const { activeTab, onChangeTab } = useJobOverview();

  const {
    jobPosting,
    hasCandidatesToHire,
    hasCandidateScheduledOnsiteInterviews,
    hasScheduledOnsiteInterviews,
    isLoading,
  } = useJobOverviewData(postingId);

  const {
    currentStep: closeJobStep,
    selectedCandidateIds,
    hasHires,
    isClosingJob,
    startFlow: startCloseJobFlow,
    handleSelectCandidates,
    handleGoBack,
    handleConfirmClose,
    handleFeedbackSkip,
    handleFeedbackSubmit,
    closeFlow: endCloseJobFlow,
  } = useCloseJobFlow({ postingId, hasCandidatesToHire });
  const { fromOnsiteInterviewCreation } = useSafeLocationState();

  const { title, professionType, subProfession } = jobPosting || {};

  const professionDisplay = useJobProfessionDisplay({
    profession: professionType || ProfessionTypeEnum.RDH,
    subProfession,
  });

  const handleCloseJobSuccess = () => {
    endCloseJobFlow();
    // Invalidate query to refetch latest data in parent component
    queryClient.invalidateQueries({
      queryKey: ['GET_JOB_POSTING_DETAIL', postingId],
    });
  };

  // Prevent back navigation to creation routes
  useBlockBackNavigation({
    blockedRoutePatterns: BLOCKED_ROUTE_PATTERNS,
    redirectTo: '/',
  });

  if (isLoading) {
    return (
      <Center flex={1}>
        <Spinner centered variant="default" size="md" />
      </Center>
    );
  }

  return (
    <Screen>
      <FloatingContainer borderRadius={0} paddingBlock={48} paddingInline={16} height="100%">
        <Row position="relative" alignItems="center" justifyContent="center">
          <Box position="absolute" left={0}>
            <BackButton
              url={fromOnsiteInterviewCreation ? '/permanent-jobs/postings' : undefined}
            />
          </Box>
          <Typography as="span" bold size="heading_2" textAlign="center">
            Job Overview
          </Typography>
        </Row>
        <JobOverviewHeader
          closedAt={jobPosting?.closedAt ? new Date(jobPosting?.closedAt) : undefined}
          hasScheduledOnsiteInterviews={hasScheduledOnsiteInterviews}
          id={postingId || ''}
          officeName={jobPosting?.officeName || ''}
          postedAt={jobPosting?.publishedAt ? new Date(jobPosting?.publishedAt) : undefined}
          professional={professionDisplay || ''}
          status={jobPosting?.status as JobPostingStatus}
          suspendedAt={jobPosting?.suspendedAt ? new Date(jobPosting?.suspendedAt) : undefined}
          officeId={jobPosting?.officeId || undefined}
          officePlanId={jobPosting?.officePlanId || undefined}
          title={title || ''}
          onCloseJob={startCloseJobFlow}
        />
        <Tabs defaultValue={TAB.JOB_DETAILS} value={activeTab} onValueChange={onChangeTab}>
          <Tabs.List
            variant="segmented"
            style={{
              backgroundColor: colors.neutral[50],
              borderRadius: 9,
              paddingBlock: 3,
              paddingInline: 8,
            }}
          >
            <Tabs.Tab value={TAB.JOB_DETAILS} data-cy="job-details-tab">
              Job Details
            </Tabs.Tab>
            <Tabs.Tab value={TAB.CANDIDATES} data-cy="candidates-tab">
              Candidates
            </Tabs.Tab>
            <Tabs.Tab value={TAB.WORKING_INTERVIEWS} data-cy="onsite-interviews-tab">
              Onsite Interviews
            </Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value={TAB.JOB_DETAILS} style={{ width: '100%' }}>
            <JobDetailsTabView jobPosting={jobPosting} />
          </Tabs.Panel>
          <Tabs.Panel value={TAB.CANDIDATES} style={{ width: '100%' }}>
            <CandidateTabView
              jobPostingId={postingId || ''}
              officeId={jobPosting?.officeId || ''}
            />
          </Tabs.Panel>
          <Tabs.Panel value={TAB.WORKING_INTERVIEWS} style={{ width: '100%' }}>
            <OnsiteInterviewTabView jobPostingId={postingId || ''} />
          </Tabs.Panel>
        </Tabs>
      </FloatingContainer>

      {closeJobStep === 'select-candidates' && (
        <SelectCandidatesPopup
          jobPostingId={postingId || ''}
          onClose={endCloseJobFlow}
          onConfirm={handleSelectCandidates}
          defaultSelectedCandidateIds={selectedCandidateIds}
        />
      )}

      {closeJobStep === 'confirm-close' && (
        <ConfirmCloseJobPopup
          onGoBack={handleGoBack}
          onConfirm={handleConfirmClose}
          hasScheduledOnsiteInterviews={hasCandidateScheduledOnsiteInterviews}
          isLoading={isClosingJob}
        />
      )}

      {closeJobStep === 'feedback' && (
        <CloseJobFeedbackPopup onClose={handleFeedbackSkip} onSubmit={handleFeedbackSubmit} />
      )}

      {closeJobStep === 'success' && (
        <CloseJobSuccessPopup onClose={handleCloseJobSuccess} hasHires={hasHires} />
      )}
    </Screen>
  );
};
